import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EstadoError, EstadoVacio, ListaEsqueleto } from '../../components/ui/Estados';
import { Input, Select } from '../../components/ui/Input';
import { Tabla, type Columna } from '../../components/ui/Tabla';
import { BarraFiltros } from '../../components/layout/BarraFiltros';
import { CabeceraPagina } from '../../components/layout/CabeceraPagina';
import {
  repositorioUsuarios,
  type AccesoUsuario,
  type UsuarioAdmin,
} from '../../../data/repositories/RepositorioUsuarios';
import { repositorioCatalogos } from '../../../data/repositories/RepositorioCatalogos';
import { Rol, ROLES_CANONICOS, type RolCanonico } from '../../../domain/value-objects/Rol';
import { ErrorApp, registrarError } from '../../../domain/errores';
import { useSesionStore } from '../../stores/sesionStore';
import { FormularioUsuario, type ResultadoFormularioUsuario } from './FormularioUsuario';
import { PermisosUsuario } from './PermisosUsuario';
import estilos from '../../components/layout/PaginaCrud.module.css';
import { FichaRegistro } from '../../components/layout/FichaRegistro';

export function Usuarios() {
  const clienteConsultas = useQueryClient();
  const politica = useSesionStore((s) => s.politica);
  const perfilActual = useSesionStore((s) => s.perfil);
  const puedeGestionar = politica?.puede('gestionar_usuarios') ?? false;
  const sinPermiso = puedeGestionar ? null : 'Tu rol no puede gestionar usuarios';

  const [busqueda, setBusqueda] = useState('');

  // Registro cuya ficha de consulta esta abierta.
  const [enFicha, setEnFicha] = useState<UsuarioAdmin | null>(null);
  const [rolFiltro, setRolFiltro] = useState<RolCanonico | ''>('');
  const [clienteFiltro, setClienteFiltro] = useState<number | null>(null);
  const [estadoFiltro, setEstadoFiltro] = useState<
    'todos' | 'activos' | 'inactivos' | 'bloqueados'
  >('todos');

  const [formularioAbierto, setFormularioAbierto] = useState(false);
  const [enEdicion, setEnEdicion] = useState<UsuarioAdmin | null>(null);
  const [enPermisos, setEnPermisos] = useState<UsuarioAdmin | null>(null);

  const filtros = useMemo(
    () => ({
      rol: rolFiltro || null,
      clienteId: clienteFiltro,
      activo: estadoFiltro === 'activos' ? true : estadoFiltro === 'inactivos' ? false : null,
      bloqueado: estadoFiltro === 'bloqueados' ? true : null,
    }),
    [rolFiltro, clienteFiltro, estadoFiltro],
  );

  const usuarios = useQuery({
    queryKey: ['usuarios', filtros],
    queryFn: () => repositorioUsuarios.listar(filtros),
  });

  const clientes = useQuery({
    queryKey: ['catalogo', 'clientes'],
    queryFn: () => repositorioCatalogos.clientes(),
    staleTime: 5 * 60_000,
  });

  const refrescar = () => {
    void clienteConsultas.invalidateQueries({ queryKey: ['usuarios'] });
  };

  const guardar = useMutation({
    mutationFn: async (datos: ResultadoFormularioUsuario) => {
      if (enEdicion) {
        await repositorioUsuarios.actualizarPerfil(enEdicion.id, {
          nombre: datos.nombre,
          telefono: datos.telefono,
          rol: datos.rol,
          clienteId: datos.clienteId,
        });
        return;
      }

      await repositorioUsuarios.crear({
        email: datos.email,
        claveInicial: datos.claveInicial,
        nombre: datos.nombre,
        rol: datos.rol,
        clienteId: datos.clienteId,
        telefono: datos.telefono,
      });
    },
    onSuccess: () => {
      toast.success(enEdicion ? 'Usuario actualizado' : 'Usuario creado');
      setFormularioAbierto(false);
      setEnEdicion(null);
      refrescar();
    },
    onError: (e) => {
      registrarError('usuarios.guardar', e);
      toast.error(e instanceof ErrorApp ? e.message : 'No se pudo guardar el usuario');
    },
  });

  const cambiarEstado = useMutation({
    mutationFn: ({
      id,
      cambios,
    }: {
      id: string;
      cambios: { activo?: boolean; bloqueado?: boolean; debeCambiarClave?: boolean };
    }) => repositorioUsuarios.actualizarEstado(id, cambios),
    onSuccess: () => {
      toast.success('Cambio aplicado');
      refrescar();
    },
    onError: (e) => {
      registrarError('usuarios.estado', e);
      toast.error(e instanceof ErrorApp ? e.message : 'No se pudo aplicar el cambio');
    },
  });

  const guardarPermisos = useMutation({
    mutationFn: async ({
      id,
      acceso,
      verUbicaciones,
    }: {
      id: string;
      acceso: AccesoUsuario;
      verUbicaciones: boolean;
    }) => {
      await repositorioUsuarios.guardarAccesos(id, acceso);
      // La visibilidad de ubicaciones vive en el perfil, no en los accesos,
      // así que se guarda por su propia vía.
      await repositorioUsuarios.cambiarVerUbicaciones(id, verUbicaciones);
    },
    onSuccess: () => {
      toast.success('Permisos guardados');
      setEnPermisos(null);
      refrescar();
    },
    onError: (e) => {
      registrarError('usuarios.permisos', e);
      toast.error(e instanceof ErrorApp ? e.message : 'No se pudieron guardar los permisos');
    },
  });

  const lista = useMemo(() => usuarios.data ?? [], [usuarios.data]);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return lista;

    return lista.filter((u) =>
      `${u.nombre} ${u.email ?? ''} ${u.telefono ?? ''} ${u.clienteNombre ?? ''}`
        .toLowerCase()
        .includes(q),
    );
  }, [lista, busqueda]);

  /** Nadie debería poder bloquearse ni desactivarse a sí mismo. */
  const esUnoMismo = (u: UsuarioAdmin) => u.id === perfilActual?.id;

  const columnas: Columna<UsuarioAdmin>[] = [
    {
      clave: 'nombre',
      encabezado: 'Usuario',
      movil: 'titulo',
      render: (u) => (
        <span>
          <span className={estilos.nombre}>{u.nombre}</span>
          {esUnoMismo(u) && <span className={estilos.secundario}> · tú</span>}
        </span>
      ),
    },
    /*
     * No hay columna de correo. `v2_list_users_by_scope` no lo devuelve —el
     * correo vive en `auth.users`, no en `perfiles`— y sólo se puede obtener
     * usuario a usuario con `v2_get_user_detail`. Una columna que siempre
     * mostraba un guión daba a entender que faltaba el dato. Se ve al abrir el
     * detalle, que es donde estaba en la aplicación anterior.
     */
    {
      clave: 'rol',
      encabezado: 'Rol',
      movil: 'detalle',
      render: (u) => (
        <Badge color={u.rol.esSuperadmin ? 'var(--status-info)' : 'var(--text-2)'}>
          {u.rol.etiqueta}
        </Badge>
      ),
    },
    {
      clave: 'cliente',
      encabezado: 'Cliente',
      // Hace de subtítulo en móvil, donde antes iba el correo.
      movil: 'subtitulo',
      render: (u) =>
        u.rol.esSuperadmin ? (
          <span className={estilos.secundario}>Todos</span>
        ) : (
          (u.clienteNombre ?? clientes.data?.find((c) => c.id === u.clienteId)?.nombre ?? '—')
        ),
    },
    {
      clave: 'estado',
      encabezado: 'Estado',
      movil: 'detalle',
      render: (u) => {
        if (u.bloqueado) return <Badge color="var(--status-danger)">Bloqueado</Badge>;
        if (!u.activo) return <Badge color="var(--text-3)">Inactivo</Badge>;
        return <Badge color="var(--status-success)">Activo</Badge>;
      },
    },
    {
      clave: 'avisos',
      encabezado: 'Pendiente',
      render: (u) =>
        u.debeCambiarClave ? (
          <Badge color="var(--status-warning)" title="Deberá cambiarla al entrar">
            Cambiar contraseña
          </Badge>
        ) : (
          <span className={estilos.secundario}>—</span>
        ),
    },
    {
      clave: 'acciones',
      encabezado: 'Acciones',
      alinear: 'centro',
      movil: 'accion',
      render: (u) => (
        <div className={estilos.acciones}>
          <Button
            tamano="sm"
            variante="secundario"
            icono="escudo"
            title="Permisos y alcance"
            motivoDeshabilitado={sinPermiso}
            onClick={() => setEnPermisos(u)}
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="ver"
            title="Ver datos"
            onClick={() => setEnFicha(u)}
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="editar"
            title="Editar datos"
            motivoDeshabilitado={sinPermiso}
            onClick={() => {
              setEnEdicion(u);
              setFormularioAbierto(true);
            }}
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="candado"
            title={u.bloqueado ? 'Desbloquear' : 'Bloquear'}
            motivoDeshabilitado={
              sinPermiso ?? (esUnoMismo(u) ? 'No puedes bloquear tu propia cuenta' : null)
            }
            onClick={() => {
              const bloquear = !u.bloqueado;
              const seguro = window.confirm(
                bloquear
                  ? `¿Bloquear a «${u.nombre}»?\n\nNo podrá entrar hasta que se le desbloquee.`
                  : `¿Desbloquear a «${u.nombre}»?`,
              );
              if (seguro) cambiarEstado.mutate({ id: u.id, cambios: { bloqueado: bloquear } });
            }}
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="llave"
            title="Obligar a cambiar la contraseña"
            motivoDeshabilitado={
              sinPermiso ?? (u.debeCambiarClave ? 'Ya tiene el cambio pendiente' : null)
            }
            onClick={() => {
              const seguro = window.confirm(
                `¿Obligar a «${u.nombre}» a cambiar su contraseña?\n\n` +
                  'Se le pedirá una nueva la próxima vez que entre.',
              );
              if (seguro) cambiarEstado.mutate({ id: u.id, cambios: { debeCambiarClave: true } });
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className={estilos.pagina}>
      <CabeceraPagina
        titulo="Usuarios"
        descripcion="Quién puede entrar, con qué rol y hasta dónde alcanza."
        acciones={
          <>
            <Button
              variante="secundario"
              icono="refrescar"
              compactoEnMovil
              onClick={refrescar}
              cargando={usuarios.isFetching}
            >
              Actualizar
            </Button>
            <Button
              icono="agregar"
              compactoEnMovil
              motivoDeshabilitado={sinPermiso}
              onClick={() => {
                setEnEdicion(null);
                setFormularioAbierto(true);
              }}
            >
              Nuevo usuario
            </Button>
          </>
        }
      />

      <BarraFiltros>
        <Input
          etiqueta="Buscar"
          icono="buscar"
          type="search"
          name="filtro-listado"
          sinAutocompletar
          placeholder="Nombre, correo o teléfono"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <Select
          etiqueta="Rol"
          placeholder="Todos los roles"
          value={rolFiltro}
          onChange={(e) => setRolFiltro(e.target.value as RolCanonico | '')}
          opciones={ROLES_CANONICOS.map((r) => ({ valor: r, etiqueta: Rol.from(r).etiqueta }))}
        />
        <Select
          etiqueta="Cliente"
          placeholder="Todos los clientes"
          value={clienteFiltro ?? ''}
          onChange={(e) => setClienteFiltro(e.target.value ? Number(e.target.value) : null)}
          opciones={(clientes.data ?? []).map((c) => ({ valor: c.id, etiqueta: c.nombre }))}
        />
        <Select
          etiqueta="Estado"
          value={estadoFiltro}
          onChange={(e) => setEstadoFiltro(e.target.value as typeof estadoFiltro)}
          opciones={[
            { valor: 'todos', etiqueta: 'Todos' },
            { valor: 'activos', etiqueta: 'Activos' },
            { valor: 'inactivos', etiqueta: 'Inactivos' },
            { valor: 'bloqueados', etiqueta: 'Bloqueados' },
          ]}
        />
      </BarraFiltros>

      {usuarios.isLoading ? (
        <ListaEsqueleto filas={6} />
      ) : usuarios.isError ? (
        <EstadoError
          acciones={
            <Button variante="secundario" icono="refrescar" onClick={refrescar}>
              Reintentar
            </Button>
          }
        />
      ) : filtrados.length === 0 ? (
        <EstadoVacio
          icono="usuarios"
          titulo={lista.length === 0 ? 'Sin usuarios' : 'Ninguno coincide'}
          descripcion={
            lista.length === 0
              ? 'Crea el primer usuario para que alguien pueda entrar a la consola.'
              : 'Prueba a cambiar la búsqueda o los filtros.'
          }
        />
      ) : (
        <>
          <Tabla
            datos={filtrados}
            columnas={columnas}
            claveFila={(u) => u.id}
            etiqueta="Usuarios"
          />

          <FichaRegistro
            item={enFicha}
            titulo="Usuario"
            columnas={columnas}
            onCerrar={() => setEnFicha(null)}
          />
        </>
      )}

      <FormularioUsuario
        abierto={formularioAbierto}
        usuario={enEdicion}
        clientes={clientes.data ?? []}
        guardando={guardar.isPending}
        onCerrar={() => {
          setFormularioAbierto(false);
          setEnEdicion(null);
        }}
        onGuardar={(datos) => guardar.mutate(datos)}
      />

      <PermisosUsuario
        usuario={enPermisos}
        guardando={guardarPermisos.isPending}
        onCerrar={() => setEnPermisos(null)}
        onGuardar={(acceso, verUbicaciones) => {
          if (!enPermisos) return;
          guardarPermisos.mutate({ id: enPermisos.id, acceso, verUbicaciones });
        }}
      />
    </div>
  );
}
